import { useRef, useState } from 'react'
import type { FormEvent } from 'react'
import { usePostHog } from '@posthog/react'
import { getBrowserTimeZone } from '../utils/timezone'
import { initScribblSignup, confirmScribblSignup } from '../api/user'
import PhoneVerificationPopup from '../components/PhoneVerificationPopup'
import '../style/views/SignupView.css'
import '../style/components/PasswordRequirements.css'

interface SignUpFormViewProps {
  onAuthResolved?: (session: {
    email: string | null
    exp?: number
    token: string
    userId: string | null
    firstName?: string
    lastName?: string
    provider?: 'google' | 'azure' | 'apple' | null
  }) => void
  onBackToLogin?: () => void
}

type SignupPayload = {
  email: string
  cell: string
  first_name: string
  last_name: string
  pin: string
  timezone: string
}

export default function SignUpFormView({ onAuthResolved, onBackToLogin }: SignUpFormViewProps) {
  const posthog = usePostHog()
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [cellDigits, setCellDigits] = useState<string[]>(Array(10).fill(''))
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [pin, setPin] = useState<string[]>(['', '', '', ''])
  const [phoneVerifyOpen, setPhoneVerifyOpen] = useState(false)
  const [pendingPayload, setPendingPayload] = useState<SignupPayload | null>(null)
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const [smsConsent, setSmsConsent] = useState(false)
  const [consentError, setConsentError] = useState<string | null>(null)
  const [status, setStatus] = useState<{ type: 'error' | 'success'; message: string } | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [passwordFocused, setPasswordFocused] = useState(false)
  const [confirmPasswordFocused, setConfirmPasswordFocused] = useState(false)
  const pinRefs = useRef<Array<HTMLInputElement | null>>([])
  const phoneRefs = useRef<Array<HTMLInputElement | null>>([])

  const passwordRequirements = {
    hasUpper: /[A-Z]/.test(password),
    hasLower: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    isMinLength: password.length >= 8,
  }

  const isPasswordValid = Object.values(passwordRequirements).every(Boolean)

  const updateCellDigit = (index: number, value: string) => {
    const digit = value.slice(-1).replace(/[^0-9]/g, '')
    const next = [...cellDigits]
    next[index] = digit
    setCellDigits(next)
    if (digit && index < cellDigits.length - 1) {
      phoneRefs.current[index + 1]?.focus()
    }
  }

  const handleCellKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace') {
      event.preventDefault()
      if (cellDigits[index]) {
        const next = [...cellDigits]
        next[index] = ''
        setCellDigits(next)
      } else if (index > 0) {
        const next = [...cellDigits]
        next[index - 1] = ''
        setCellDigits(next)
        phoneRefs.current[index - 1]?.focus()
      }
    }
  }

  const updatePin = (index: number, value: string) => {
    const digit = value.slice(-1).replace(/[^0-9]/g, '')
    const next = [...pin]
    next[index] = digit
    setPin(next)
    if (digit && index < pin.length - 1) {
      pinRefs.current[index + 1]?.focus()
    }
  }

  const handlePinKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace') {
      event.preventDefault()
      if (pin[index]) {
        const next = [...pin]
        next[index] = ''
        setPin(next)
      } else if (index > 0) {
        const next = [...pin]
        next[index - 1] = ''
        setPin(next)
        pinRefs.current[index - 1]?.focus()
      }
    }
  }

  const handlePhoneVerified = async () => {
    if (!pendingPayload) {
      throw new Error('Missing signup details. Please try again.')
    }

    setSubmitting(true)
    try {
      const signupJson = await confirmScribblSignup({
        email,
        password,
        firstName,
        lastName,
        phone: pendingPayload.cell,
        pin: pendingPayload.pin,
        timezone: pendingPayload.timezone,
        source: 'web',
      })

      const sessionToken = signupJson.token || signupJson.session_token

      if (!sessionToken) {
        throw new Error('No session token returned')
      }

      posthog?.capture('user_signed_up', {
        email: signupJson.email || email,
        first_name: firstName,
        last_name: lastName,
      })
      setStatus({ type: 'success', message: 'Account saved. Redirecting…' })
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('myscribbl_onboarding_status', 'pending')
      }

      if (onAuthResolved) {
        onAuthResolved({
          email: signupJson.email || email,
          token: sessionToken,
          exp: signupJson.expiresAt
            ? Number(signupJson.expiresAt)
            : signupJson.expires_at
              ? Number(signupJson.expires_at)
              : undefined,
          userId: signupJson.userId || signupJson.user_id || null,
          firstName: signupJson.firstName || signupJson.first_name || firstName,
          lastName: signupJson.lastName || signupJson.last_name || lastName,
          provider: 'azure',
        })
      }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO(ENG-399): replace with proper type
    } catch (err: any) {
      posthog?.captureException(err, { context: 'signup' })
      console.error('Signup error:', err)
      let errorMessage = err.message || 'Failed to create account. Please try again.'
      
      if (errorMessage.includes('Azure authentication failed: 400')) {
        errorMessage = 'Incorrect email or password'
      }
      
      setStatus({ type: 'error', message: errorMessage })
    } finally {
      setSubmitting(false)
    }
  }

  const handlePhoneVerifyClose = () => {
    setPhoneVerifyOpen(false)
    setPendingPayload(null)
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setStatus(null)
    setConsentError(null)
    if (!acceptedTerms || !smsConsent) {
      setConsentError('Please agree to the Privacy Policy, Terms, and SMS updates to continue.')
      return
    }
    const trimmedPin = pin.join('').trim()
    const rawCell = cellDigits.join('').trim()
    const formattedCell = rawCell.length >= 10 
      ? `+1${rawCell.slice(0, 10)}` 
      : ''
    const browserTimezone = getBrowserTimeZone()
    if (!firstName.trim() || !lastName.trim() || !email.trim() || rawCell.length < 10) {
      setStatus({ type: 'error', message: 'Please fill out all fields.' })
      return
    }
    if (!isPasswordValid) {
      setStatus({ type: 'error', message: 'Password does not meet requirements.' })
      return
    }
    if (password !== confirmPassword) {
      setStatus({ type: 'error', message: 'Passwords do not match.' })
      return
    }
    if (!/^[0-9]{4}$/.test(trimmedPin)) {
      setStatus({ type: 'error', message: 'PIN must be exactly 4 digits.' })
      return
    }

    setSubmitting(true)
    try {
      // Step 1: Initiate signup (check email availability)
      try {
        await initScribblSignup(email)
      } catch (err) {
        const status = (err as { status?: number }).status
        if (status === 409) {
          setStatus({ type: 'error', message: 'This email address is already registered.' })
          return
        }
        throw new Error('Unable to start signup. Please try again.')
      }

      const payload: SignupPayload = {
        email,
        cell: formattedCell,
        first_name: firstName,
        last_name: lastName,
        pin: trimmedPin,
        timezone: browserTimezone,
      }
      setPendingPayload(payload)
      setPhoneVerifyOpen(true)
    } catch (err) {
      console.error('Failed to open phone verification', err)
      setPendingPayload(null)
      setStatus({
        type: 'error',
        message: err instanceof Error ? err.message : 'Unable to start signup. Please try again.',
      })
      setPhoneVerifyOpen(false)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="signup-shell">
      <div className="signup-panel">
        <div className="signup-layout">
          <div className="signup-hero">
            <button type="button" className="signup-signout-button" onClick={onBackToLogin}>
              Back to login
            </button>
            <p className="eyebrow">Welcome</p>
            <h1>Finish setting up your account</h1>
            <p className="muted">
              Add a few details below to get started with myScribbl.
            </p>
          </div>

          <div className="card signup-card">
            <form onSubmit={handleSubmit} className="signup-form">
              <div className="form-grid">
                <label className="full-width">
                  <span>Email</span>
                  <input
                    type="email"
                    name="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="name@company.com"
                  />
                </label>
                <label>
                  <span>First name</span>
                  <input 
                    name="given-name"
                    autoComplete="given-name"
                    value={firstName} 
                    onChange={(e) => setFirstName(e.target.value)} 
                  />
                </label>
                <label>
                  <span>Last name</span>
                  <input 
                    name="family-name"
                    autoComplete="family-name"
                    value={lastName} 
                    onChange={(e) => setLastName(e.target.value)} 
                  />
                </label>
                <label className="full-width password-label">
                  <span>Password</span>
                  <input
                    type="password"
                    name="new-password"
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onFocus={() => setPasswordFocused(true)}
                    onBlur={() => setPasswordFocused(false)}
                    placeholder="Create a password"
                  />
                  {passwordFocused && (
                    <div className="password-requirements">
                      <p className={passwordRequirements.isMinLength ? 'met' : ''}>• At least 8 characters</p>
                      <p className={passwordRequirements.hasUpper ? 'met' : ''}>• At least one uppercase letter</p>
                      <p className={passwordRequirements.hasLower ? 'met' : ''}>• At least one lowercase letter</p>
                      <p className={passwordRequirements.hasNumber ? 'met' : ''}>• At least one number</p>
                      <p className={passwordRequirements.hasSpecial ? 'met' : ''}>• At least one special character</p>
                    </div>
                  )}
                </label>
                <label className="full-width password-label">
                  <span>Confirm Password</span>
                  <input
                    type="password"
                    name="confirm-password"
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    onFocus={() => setConfirmPasswordFocused(true)}
                    onBlur={() => setConfirmPasswordFocused(false)}
                    placeholder="Confirm your password"
                  />
                  {confirmPasswordFocused && (
                    <div className="password-requirements">
                      <p className={password === confirmPassword && confirmPassword !== '' ? 'met' : ''}>
                        {password === confirmPassword && confirmPassword !== '' ? '• Passwords match' : '• Passwords do not match'}
                      </p>
                    </div>
                  )}
                </label>
              </div>

              <div className="form-row-block">
                <label className="full-row">
                  <span>Phone number</span>
                  <div className="phone-row">
                    {cellDigits.map((digit, pos) => (
                      <input
                        key={`phone-${pos}`}
                        className="pin-input"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => updateCellDigit(pos, e.target.value)}
                        onKeyDown={(e) => handleCellKeyDown(pos, e)}
                        ref={(el) => {
                          phoneRefs.current[pos] = el
                        }}
                      />
                    ))}
                  </div>
                </label>
              </div>

              <div className="form-row-block">
                <label className="full-row">
                  <span>4-digit PIN</span>
                  <div className="pin-row">
                    {pin.map((digit, pos) => (
                      <input
                        key={`pin-${pos}`}
                        className="pin-input"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => updatePin(pos, e.target.value)}
                        onKeyDown={(e) => handlePinKeyDown(pos, e)}
                        ref={(el) => {
                          pinRefs.current[pos] = el
                        }}
                      />
                    ))}
                  </div>
                </label>
              </div>

              <div className="form-divider" />

              <div className="form-row-block">
                <div className="signup-consent-list">
                  <div className="signup-consent">
                    <input
                      id="signup-consent"
                      type="checkbox"
                      checked={acceptedTerms}
                      onChange={(e) => {
                        const checked = e.target.checked
                        setAcceptedTerms(checked)
                        if (checked && smsConsent) setConsentError(null)
                      }}
                    />
                    <label htmlFor="signup-consent">
                      I agree to the{' '}
                      <a
                        href="https://www.myscribbl.com/privacy-policy"
                        target="_blank"
                        rel="noreferrer"
                      >
                        Privacy Policy
                      </a>{' '}
                      and{' '}
                      <a
                        href="https://www.myscribbl.com/terms-conditions"
                        target="_blank"
                        rel="noreferrer"
                      >
                        Terms &amp; Conditions
                      </a>
                      .
                    </label>
                  </div>
                  <div className="signup-consent">
                    <input
                      id="signup-sms-consent"
                      type="checkbox"
                      checked={smsConsent}
                      onChange={(e) => {
                        const checked = e.target.checked
                        setSmsConsent(checked)
                        if (checked && acceptedTerms) setConsentError(null)
                      }}
                    />
                    <label htmlFor="signup-sms-consent">
                      I agree to receive SMS updates from myScribbl.
                    </label>
                  </div>
                </div>
                {consentError && (
                  <span className="signup-status signup-consent-error error">{consentError}</span>
                )}
              </div>

              <div className="signup-actions">
                <button
                  type="submit"
                  className="primary-button"
                  disabled={submitting || phoneVerifyOpen}
                >
                  {submitting ? 'Starting…' : 'Create account'}
                </button>
                {status && <span className={`signup-status ${status.type}`}>{status.message}</span>}
              </div>
            </form>
          </div>
          <PhoneVerificationPopup
            open={phoneVerifyOpen}
            cell={pendingPayload?.cell ?? ''}
            onClose={handlePhoneVerifyClose}
            onVerified={handlePhoneVerified}
            description="Enter the 4-digit code sent to your phone to finish creating your account."
          />
        </div>
      </div>
    </div>
  )
}
